/**
 * render_choropleth — choropleth map of US states / congressional
 * districts / counties, colored per the AI's value_by_key map.
 *
 * Powered by mapsmcp shapes via the server-side proxy at
 * /api/v1/shapes/:collection — the proxy caches GeoJSON in memory + on
 * disk + warms two hot collections (us-states, us-cd119) on boot, so
 * the leaflet render path doesn't pay the 30-90s mapsmcp build cost.
 *
 * Composable as a `renderer` panel kind inside compose_panel:
 *   { kind: "renderer", tool: "render_choropleth",
 *     input: { collection: "us-states",
 *              value_by_key: { CA: { fill: "#1d4ed8" }, ... },
 *              legend: [{ label: "Strongly support", color: "#1d4ed8" }] }}
 *
 * Graceful degradation: when MAPS_MCP_TOKEN is not configured, the
 * shapes endpoint returns 503 with a hint. We render that hint as an
 * amber setup panel rather than a broken empty map — the rest of the
 * composition still ships.
 */

import { useQuery } from "@tanstack/react-query";
import { MapContainer, TileLayer, GeoJSON as LeafletGeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import type { FeatureCollection, Feature } from "geojson";
import type { PathOptions } from "leaflet";
import { registerRenderer, type RendererProps } from "./registry.js";

export interface ChoroplethValueEntry {
  /** Fill color (any CSS color — hex, hsl, named). */
  fill: string;
  /** Border color. Defaults to a slightly darker stroke than the fill. */
  stroke?: string;
  /** Tooltip text for this feature. */
  label?: string;
  /** Optional weight (border thickness in px). Default 1. */
  weight?: number;
}

export interface ChoroplethLegendEntry {
  label: string;
  color: string;
}

export interface ChoroplethInput {
  /** mapsmcp collection slug. Currently supported by the shape proxy:
   *  "us-states", "us-cd113"…"us-cd119", "us-counties". */
  collection: string;
  /** Optional card title. */
  title?: string;
  /** Optional subtitle / lead-in line. */
  subtitle?: string;
  /**
   * Per-feature styling. Key shape depends on the collection:
   *   us-states  → 2-letter state code, e.g. "CA"
   *   us-cd119   → "STATE-DISTRICT", e.g. "CA-12" (or GEOID20)
   *   us-counties→ FIPS GEOID, e.g. "06037"
   * Features missing from this map render in a muted neutral.
   */
  value_by_key: Record<string, ChoroplethValueEntry>;
  /** Optional legend entries shown below the map. */
  legend?: ChoroplethLegendEntry[];
  /** Map height in pixels. Default 420. */
  height?: number;
  /** Footer line (e.g. "Data as of 2026-05-09 · Whip count snapshot"). */
  footer?: string;
}

const DEFAULT_FEATURE_STYLE: ChoroplethValueEntry = {
  fill: "hsl(0 0% 92%)",
  stroke: "hsl(215 16% 70%)",
  weight: 1,
};

// Extract the join key from a GeoJSON feature based on the collection.
// mapsmcp's properties differ slightly across shape sets — we look at
// the most common keys for each.
function getKeyFor(collection: string): (f: Feature) => string | null {
  if (collection === "us-states") {
    return (f) => {
      const p = (f.properties as Record<string, unknown> | null) ?? {};
      return (p.STUSPS as string | undefined) ?? (p.state as string | undefined) ?? null;
    };
  }
  if (collection.startsWith("us-cd")) {
    return (f) => {
      const p = (f.properties as Record<string, unknown> | null) ?? {};
      const stateAbbr = (p.STATE_ABBR ?? p.state_abbr ?? p.STUSPS ?? p.state) as string | undefined;
      const district =
        (p.CD119FP ?? p.CD118FP ?? p.CD117FP ?? p.CD116FP ?? p.CD115FP ?? p.CD114FP ?? p.CD113FP ?? p.district ?? p.DISTRICT) as
          | string
          | number
          | undefined;
      if (stateAbbr && district != null) {
        const districtNum = parseInt(String(district), 10);
        if (Number.isFinite(districtNum)) return `${stateAbbr}-${districtNum}`;
      }
      return (p.GEOID20 as string | undefined) ?? (p.GEOID as string | undefined) ?? null;
    };
  }
  if (collection === "us-counties") {
    return (f) => {
      const p = (f.properties as Record<string, unknown> | null) ?? {};
      return (p.GEOID as string | undefined) ?? (p.fips as string | undefined) ?? null;
    };
  }
  return () => null;
}

interface ShapesError {
  error: string;
  hint?: string;
}

function ChoroplethCard({ input }: RendererProps<ChoroplethInput>) {
  const collection = input.collection;
  const valueByKey = input.value_by_key ?? {};
  const height = input.height ?? 420;
  const legend = input.legend ?? [];

  const { data, isLoading, error, dataUpdatedAt, refetch } = useQuery<FeatureCollection | ShapesError>({
    queryKey: ["shapes", collection],
    queryFn: async () => {
      const res = await fetch(`/api/v1/shapes/${encodeURIComponent(collection)}`, { credentials: "include" });
      if ([502, 503, 504].includes(res.status)) {
        const payload = (await res.json().catch(() => ({}))) as Partial<ShapesError>;
        return {
          error: payload.error ?? `mapsmcp shape proxy returned ${res.status}`,
          hint:
            "The map service is temporarily unavailable for this view. Retry the map or continue with the source data.",
        };
      }
      if (!res.ok) throw new Error(`shapes ${collection} failed: ${res.status}`);
      const body = await res.json();
      if (!body || body.type !== "FeatureCollection" || !Array.isArray(body.features)) {
        throw new Error("The map service returned an invalid shape response.");
      }
      return body as FeatureCollection;
    },
    staleTime: 60 * 60 * 1000, // 1h client cache (proxy caches longer)
    retry: false,
  });

  const getKey = getKeyFor(collection);

  const styleFn = (feature?: Feature): PathOptions => {
    const key = feature ? getKey(feature) : null;
    const entry = (key && valueByKey[key]) || DEFAULT_FEATURE_STYLE;
    return {
      color: entry.stroke ?? entry.fill,
      fillColor: entry.fill,
      weight: entry.weight ?? 1,
      fillOpacity: 0.82,
      opacity: 1,
    };
  };

  const onEachFeature = (feature: Feature, layer: any) => {
    const key = getKey(feature);
    const entry = key ? valueByKey[key] : null;
    const label = entry?.label ?? key ?? "";
    // Leaflet interprets tooltip strings as HTML. Source labels are data.
    if (label) {
      const tooltip = document.createElement("span");
      tooltip.textContent = label;
      layer.bindTooltip(tooltip, { sticky: true });
    }
    layer.on({
      mouseover: (e: any) => { e.target.setStyle({ weight: 2.5 }); e.target.bringToFront(); },
      mouseout: (e: any) => { e.target.setStyle({ weight: entry?.weight ?? 1 }); },
    });
  };

  const Header = () => (
    (input.title || input.subtitle) ? (
      <div className="px-4 pt-3 pb-2">
        {input.title && <div className="text-base font-semibold text-stone-900">{input.title}</div>}
        {input.subtitle && <div className="text-xs text-stone-500 mt-0.5">{input.subtitle}</div>}
      </div>
    ) : null
  );
  const Footer = () => (
    <>
      {legend.length > 0 && (
        <div className="px-4 py-2 border-t border-stone-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-stone-600">
          {legend.map((l, i) => (
            <span key={i} className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: l.color }} aria-hidden />
              <span>{l.label}</span>
            </span>
          ))}
        </div>
      )}
      {input.footer && (
        <div className="px-4 py-2 border-t border-stone-100 text-[11px] text-stone-500 italic">
          {input.footer}
        </div>
      )}
    </>
  );

  if (isLoading) {
    return (
      <div className="self-stretch border border-stone-200 rounded-xl bg-white overflow-hidden">
        <Header />
        <div className="px-4 text-stone-400 italic text-sm" style={{ height }}>
          Loading {collection} shapes…
        </div>
        <Footer />
      </div>
    );
  }
  if (error) {
    return (
      <div className="self-stretch border border-red-200 rounded-xl bg-red-50 overflow-hidden">
        <Header />
        <div role="alert" className="p-6 text-red-800 text-sm" style={{ minHeight: height / 3 }}>
          Could not load <span className="font-mono">{collection}</span> shapes:{" "}
          {(error as Error).message}
          <button type="button" onClick={() => { void refetch(); }} className="block mt-3 underline">Retry map</button>
        </div>
      </div>
    );
  }
  if (data && (data as ShapesError).error) {
    const e = data as ShapesError;
    return (
      <div className="self-stretch border border-amber-200 rounded-xl bg-amber-50 overflow-hidden">
        <Header />
        <div role="alert" className="p-6" style={{ minHeight: height / 3 }}>
          <div className="font-semibold text-amber-900 mb-1">Real-shape map not available</div>
          <div className="text-sm text-amber-800">{e.error}</div>
          {e.hint && <div className="text-xs text-amber-700 mt-1.5">{e.hint}</div>}
          <button type="button" onClick={() => { void refetch(); }} className="block mt-3 underline">Retry map</button>
        </div>
      </div>
    );
  }

  const fc = data as FeatureCollection;

  if (!fc?.features?.length) {
    return <div role="status" className="self-stretch border border-stone-200 rounded-xl bg-white p-4 text-sm text-stone-600"><Header />No geographic features were returned for this view.<Footer /></div>;
  }

  return (
    <div className="self-stretch border border-stone-200 rounded-xl bg-white overflow-hidden">
      <Header />
      <div style={{ height }}>
        <MapContainer
          center={[38.8, -96.5]}
          zoom={4}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://carto.com/">CARTO</a> · <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          {fc.features.length > 0 && (
            <LeafletGeoJSON
              key={`${collection}-${dataUpdatedAt}-${JSON.stringify(valueByKey)}`}
              data={fc}
              style={styleFn as any}
              onEachFeature={onEachFeature}
            />
          )}
        </MapContainer>
      </div>
      <Footer />
    </div>
  );
}

registerRenderer<ChoroplethInput>({
  tool: "render_choropleth",
  label: "Choropleth map of US states / districts / counties via mapsmcp",
  Component: ChoroplethCard,
});

export { ChoroplethCard };
